import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { MasterDataListItem } from '../../components/MasterDataListItem';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useDesignations } from '../../hooks/useDesignations';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'DesignationList'>;

export function DesignationListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const list = useDesignations();

  const footer = useMemo(() => {
    if (!list.isLoadingMore) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [list.isLoadingMore, palette.primary]);

  const hasFilters =
    Boolean(list.search.trim()) ||
    list.filters.isActive != null ||
    list.filters.isManagerial != null;

  return (
    <PermissionGuard
      permission={PERMISSIONS.DESIGNATION_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Designations"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
        <AppInput
          label="Search"
          value={list.search}
          onChangeText={list.setSearch}
          placeholder="Code or name"
          autoCapitalize="none"
        />
        <View style={styles.filterRow}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Show active designations"
            onPress={() => list.applyFilters({ isActive: true })}
            style={themed.chip}
          >
            <Text style={themed.chipText}>Active</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Show inactive designations"
            onPress={() => list.applyFilters({ isActive: false })}
            style={themed.chip}
          >
            <Text style={themed.chipText}>Inactive</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Show managerial designations"
            onPress={() => list.applyFilters({ isManagerial: true })}
            style={themed.chip}
          >
            <Text style={themed.chipText}>Managerial</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Clear filters"
            onPress={() => list.applyFilters({})}
            style={themed.chip}
          >
            <Text style={themed.chipText}>All</Text>
          </Pressable>
        </View>

        {capabilities.canCreateDesignation ? (
          <AppButton
            title="Create designation"
            onPress={() => navigation.navigate(routes.designationCreate)}
          />
        ) : null}

        {list.isLoading && list.items.length === 0 ? (
          <View style={styles.center}>
            <ActivityIndicator color={palette.primary} />
          </View>
        ) : list.error && list.items.length === 0 ? (
          <ErrorState message={list.error} onRetry={list.retry} />
        ) : (
          <FlatList
            data={list.items}
            keyExtractor={item => String(item.designationId)}
            refreshControl={
              <RefreshControl
                refreshing={list.isRefreshing}
                onRefresh={list.refresh}
              />
            }
            onEndReached={list.loadMore}
            onEndReachedThreshold={0.4}
            ListFooterComponent={footer}
            ListEmptyComponent={
              <EmptyState
                title={
                  hasFilters
                    ? 'No records match the current search.'
                    : 'No designations were found.'
                }
                message={
                  hasFilters
                    ? 'Try clearing search or filters.'
                    : 'Create a designation when you have permission.'
                }
                actionTitle={hasFilters ? 'Reset filters' : 'Retry'}
                onAction={hasFilters ? list.resetFilters : list.retry}
              />
            }
            contentContainerStyle={styles.listContent}
            renderItem={({ item }) => (
              <MasterDataListItem
                title={item.designationName}
                code={item.designationCode}
                subtitle={
                  [
                    item.departmentName,
                    item.designationLevel != null
                      ? `Level ${item.designationLevel}`
                      : null,
                    item.isManagerial ? 'Managerial' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || null
                }
                status={item.statusLabel}
                onPress={() =>
                  navigation.navigate(routes.designationDetails, {
                    designationId: item.designationId,
                  })
                }
              />
            )}
          />
        )}

        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
